import React, { useState, useRef, useEffect } from 'react';

const RangeSlider = ({ 
  label, 
  min, 
  max, 
  valueMin, 
  valueMax, 
  onChangeMin, 
  onChangeMax, 
  step = 1000,
  unit = '',
  className = ""
}) => {
  const [isDraggingMin, setIsDraggingMin] = useState(false);
  const [isDraggingMax, setIsDraggingMax] = useState(false);
  const sliderRef = useRef(null);

  const formatPrice = (value) => {
    if (!value) return '';
    return value.toLocaleString('es-AR');
  };

  const handleMinChange = (e) => {
    const newValue = parseInt(e.target.value);
    if (newValue <= (valueMax || max)) {
      onChangeMin(newValue);
    }
  };

  const handleMaxChange = (e) => {
    const newValue = parseInt(e.target.value);
    if (newValue >= (valueMin || min)) {
      onChangeMax(newValue);
    }
  };

  const getPercentage = (value) => {
    return ((value - min) / (max - min)) * 100;
  };

  const minPercentage = getPercentage(valueMin || min);
  const maxPercentage = getPercentage(valueMax || max);

  const handleMouseMove = (e) => {
    if (!isDraggingMin && !isDraggingMax) return;
    
    const rect = sliderRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const percentage = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    const value = Math.round(min + (percentage / 100) * (max - min));
    const steppedValue = Math.round(value / step) * step;

    if (isDraggingMin && steppedValue <= (valueMax || max)) {
      onChangeMin(steppedValue);
    } else if (isDraggingMax && steppedValue >= (valueMin || min)) {
      onChangeMax(steppedValue);
    }
  };

  const handleMouseUp = () => {
    setIsDraggingMin(false);
    setIsDraggingMax(false);
  };

  useEffect(() => {
    if (isDraggingMin || isDraggingMax) {
      // Mouse events
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      // Touch events
      document.addEventListener('touchmove', handleMouseMove, { passive: false });
      document.addEventListener('touchend', handleMouseUp);
      
      return () => {
        // Clean up mouse events
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        
        // Clean up touch events
        document.removeEventListener('touchmove', handleMouseMove);
        document.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDraggingMin, isDraggingMax, valueMin, valueMax]);

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Input Fields */}
      <div className="flex gap-2">
        <div className="flex-1">
          <input
            type="text"
            value={formatPrice(valueMin)}
            onChange={(e) => {
              const rawValue = e.target.value.replace(/\./g, '');
              onChangeMin(rawValue ? parseInt(rawValue) : '');
            }}
            placeholder="Mín"
            className="w-full px-2 py-1 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
          />
        </div>
        <div className="flex-1">
          <input
            type="text"
            value={formatPrice(valueMax)}
            onChange={(e) => {
              const rawValue = e.target.value.replace(/\./g, '');
              onChangeMax(rawValue ? parseInt(rawValue) : '');
            }}
            placeholder="Máx"
            className="w-full px-2 py-1 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
          />
        </div>
      </div>

      {/* Range Slider */}
      <div className="relative h-8" ref={sliderRef}>
        {/* Track */}
        <div className="absolute top-3 left-0 right-0 h-2 bg-slate-200 rounded-full">
          {/* Active Range */}
          <div 
            className="absolute h-2 bg-sky-500 rounded-full"
            style={{
              left: `${minPercentage}%`,
              right: `${100 - maxPercentage}%`
            }}
          />
        </div>

        {/* Min Thumb */}
        <div
          className={`absolute top-1 w-6 h-6 bg-white border-2 border-sky-500 rounded-full shadow-md transition-shadow cursor-grab active:cursor-grabbing ${
            isDraggingMin ? 'shadow-lg ring-2 ring-sky-200' : 'hover:shadow-lg'
          }`}
          style={{
            left: `${minPercentage}%`,
            transform: 'translateX(-50%)',
            zIndex: isDraggingMin ? 20 : 10
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            setIsDraggingMin(true);
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            setIsDraggingMin(true);
          }}
        />

        {/* Max Thumb */}
        <div
          className={`absolute top-1 w-6 h-6 bg-white border-2 border-sky-500 rounded-full shadow-md transition-shadow cursor-grab active:cursor-grabbing ${
            isDraggingMax ? 'shadow-lg ring-2 ring-sky-200' : 'hover:shadow-lg'
          }`}
          style={{
            left: `${maxPercentage}%`,
            transform: 'translateX(-50%)',
            zIndex: isDraggingMax ? 20 : 10
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            setIsDraggingMax(true);
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            setIsDraggingMax(true);
          }}
        />

        {/* Click area for track */}
        <div
          className="absolute inset-0 cursor-pointer"
          onClick={(e) => {
            const rect = sliderRef.current.getBoundingClientRect();
            const percentage = ((e.clientX - rect.left) / rect.width) * 100;
            const value = Math.round(min + (percentage / 100) * (max - min));
            const steppedValue = Math.round(value / step) * step;
            
            const midPoint = (valueMin || min) + (valueMax || max) - (valueMin || min);
            if (steppedValue < midPoint) {
              if (steppedValue <= (valueMax || max)) {
                onChangeMin(steppedValue);
              }
            } else {
              if (steppedValue >= (valueMin || min)) {
                onChangeMax(steppedValue);
              }
            }
          }}
        />
      </div>

      {/* Labels */}
      <div className="flex justify-between text-xs text-slate-500">
        <span>{min.toLocaleString()}{unit}</span>
        <span>{max.toLocaleString()}{unit}</span>
      </div>
    </div>
  );
};

export default RangeSlider;
